import { useTradeWayLabel } from 'components/secondaryMarket/shared/status/tradeWay';
import { ListGroup, ListGroupItem } from 'components/shared/ListGroup';
import { useSPIntl } from 'hooks/useIntl';
import { FC, useCallback } from 'react';
import type { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';

export interface OrderPlacedDetailsProps {
  orderId: string;
  productIsin: string | undefined;
  productRic: string | undefined;
  fileCode: string | undefined;
  quantity: number | undefined;
  cashAmount: number | undefined;
  tradeWay: TradeWay;
  settlementDate?: string;
  tradeDate?: string;
  execTime?: string;
  cobDate?: string;
  valuationDate?: string;
  currency: string;
}

export const OrderPlacedDetails: FC<OrderPlacedDetailsProps> = ({
  orderId,
  productIsin,
  quantity,
  tradeWay,
  settlementDate,
  tradeDate,
  cashAmount,
  cobDate,
  valuationDate,
  currency,
  productRic,
  fileCode,
}) => {
  const { formatDisplayedDate, formatNumber } = useSPIntl();
  const formatCode = useCallback((code?: string) => (!code || code?.trim().length === 0 ? '-' : code), []);
  const tradeWayLabel = useTradeWayLabel({ way: tradeWay });
  return (
    <ListGroup className="h-100 overflow-y-auto">
      <ListGroupItem
        className="ps-0 pe-3 text-nowrap"
        labelKey="secondaryMarket.sidePanel.label.orderId"
        data-e2e="orderId"
      >
        <span data-e2e="orderId-value" className="ms-3" title={orderId}>
          {orderId}
        </span>
      </ListGroupItem>
      <ListGroupItem className="ps-0 pe-3" labelKey="secondaryMarket.sidePanel.label.isin" data-e2e="isin">
        <span data-e2e="isin-value">{formatCode(productIsin)}</span>
      </ListGroupItem>
      <ListGroupItem className="ps-0 pe-3" labelKey="secondaryMarket.sidePanel.label.ric" data-e2e="ric">
        <span data-e2e="ric-value">{formatCode(productRic)}</span>
      </ListGroupItem>
      <ListGroupItem className="ps-0 pe-3" labelKey="secondaryMarket.sidePanel.label.fileCode" data-e2e="fileCode">
        <span data-e2e="fileCode-value">{formatCode(fileCode)}</span>
      </ListGroupItem>
      {!!quantity && !isNaN(quantity) && (
        <ListGroupItem className="ps-0 pe-3" labelKey="secondaryMarket.sidePanel.label.quantity" data-e2e="quantity">
          <span data-e2e="quantity-value">{formatNumber(quantity)}</span>
        </ListGroupItem>
      )}
      {!!cashAmount && !isNaN(cashAmount) && (
        <ListGroupItem
          className="ps-0 pe-3"
          labelKey="secondaryMarket.sidePanel.label.cashAmount"
          data-e2e="cashAmount"
        >
          <span data-e2e="cashAmount-value">
            {formatNumber(cashAmount, {
              currency,
              style: 'currency',
              currencyDisplay: 'code',
            })}
          </span>
        </ListGroupItem>
      )}
      <ListGroupItem className="ps-0 pe-3" labelKey="secondaryMarket.sidePanel.label.tradeSide" data-e2e="tradeSide">
        <span data-e2e="tradeSide-value">{tradeWayLabel}</span>
      </ListGroupItem>
      <ListGroupItem className="ps-0 pe-3" labelKey="secondaryMarket.sidePanel.label.tradeDate" data-e2e="tradeDate">
        <span data-e2e="tradeDate-value">
          {formatDisplayedDate(tradeDate, {
            day: '2-digit',
          })}
        </span>
      </ListGroupItem>
      <ListGroupItem className="ps-0 pe-3" labelKey="secondaryMarket.sidePanel.label.cobDate" data-e2e="cobDate">
        <span data-e2e="cobDate-value">
          {formatDisplayedDate(cobDate, {
            day: '2-digit',
          })}
        </span>
      </ListGroupItem>
      <ListGroupItem
        className="ps-0 pe-3"
        labelKey="secondaryMarket.sidePanel.label.valuationDate"
        data-e2e="valuationDate"
      >
        <span data-e2e="valuationDate-value">
          {formatDisplayedDate(valuationDate, {
            day: '2-digit',
          })}
        </span>
      </ListGroupItem>

      <ListGroupItem
        className="ps-0 pe-3"
        labelKey="secondaryMarket.sidePanel.label.settlementDate"
        data-e2e="settlementDate"
      >
        <span data-e2e="settlementDate-value">
          {formatDisplayedDate(settlementDate, {
            day: '2-digit',
          })}
        </span>
      </ListGroupItem>
    </ListGroup>
  );
};
